import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { loadSettings, saveSettings } from "../lib/settings";
import { useStore } from "../lib/store";
import { primePling } from "../lib/sound";
import { getHostname, setupAutostart, isTauriSync } from "../lib/tauri";
import { isCapacitor, capacitorDeviceLabel } from "../lib/capacitor";

type Props = { onLoggedIn(): void };

export default function Login({ onLoggedIn }: Props) {
  const stored = loadSettings();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [autostart, setAutostart] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setMe = useStore((s) => s.setMe);
  const isAndroidApp = isCapacitor();
  const isWindowsApp = isTauriSync();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    // De login-klik is een user-gesture; gebruik 'm om de audio-autoplay
    // restrictie nu meteen te ontgrendelen, anders blijft de éérste
    // inkomende-bericht-pling stil.
    primePling();
    try {
      // Apparaat-label automatisch = Windows-computernaam in de Tauri-app,
      // en een duidelijke Android-label in de native APK.
      const deviceLabel = isAndroidApp
        ? capacitorDeviceLabel()
        : (await getHostname()) ?? stored.device_label ?? "desktop";
      saveSettings({ device_label: deviceLabel });
      const res = await api.login({
        username: username.trim(),
        password,
        device_label: deviceLabel,
      });
      saveSettings({ token: res.token });
      setMe(res.user);
      if (isWindowsApp) await setupAutostart(autostart);
      onLoggedIn();
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_credentials") {
        setError("Gebruikersnaam of wachtwoord onjuist.");
      } else {
        setError("Kon niet inloggen — controleer je internetverbinding.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center p-4">
      {/* Meebuigend en niet vast op 360px: op een smallere telefoon liep het
          formulier buiten het scherm, inclusief de inlogknop. */}
      <form
        onSubmit={submit}
        className="w-full max-w-[360px] space-y-4 rounded-lg bg-slate-800 p-6 shadow-xl"
      >
        <h1 className="text-xl font-semibold text-slate-100">Swift — inloggen</h1>
        <Field label="Gebruikersnaam">
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            required
          />
        </Field>
        <Field label="Wachtwoord">
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        {isWindowsApp && (
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={autostart}
              onChange={(e) => setAutostart(e.target.checked)}
            />
            Starten als Windows opstart
          </label>
        )}
        {error && <div className="text-sm text-red-400">{error}</div>}
        <button
          className="w-full rounded-md bg-accent px-4 py-2 font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          disabled={busy}
        >
          {busy ? "Bezig…" : "Inloggen"}
        </button>
      </form>
      <style>{`
        .input {
          width: 100%;
          background: #0f172a;
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 8px 10px;
          color: #e2e8f0;
          outline: none;
        }
        .input:focus { border-color: #3b82f6; }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">{label}</div>
      {children}
    </label>
  );
}
